import type { MouseEvent } from 'react'
import { runtimeConfig } from '@/config/runtimeConfig'
import { MailIcon, SparkleChatIcon } from '@features/shared/icons/icons'
import { IconButton } from '@features/shared/components/IconButton'
import { useIsModuleEnabled } from '@features/modules/hooks/useIsModuleEnabled'
import ExportsTray from '@features/exports/components/ExportsTray'
import PageShareButton from '@features/sharing/components/PageShareButton'
import { usePanelStack } from '../hooks/usePanelStack'
import { loadChatPanel } from '../logic/chatPanelChunk'

/** One class per state: the open panel's button is selected. */
function headerIconButtonClass(isOpen: boolean) {
  return isOpen ? 'app-header-icon-button-active' : 'app-header-icon-button'
}
const modifierLabel = runtimeConfig.environment.shortcutModifierLabel

/** Ctrl+click (Cmd+click on macOS) stacks panels side by side instead of replacing. */
function isAlongsideClick(event: MouseEvent) {
  return event.ctrlKey || event.metaKey
}

export default function GlobalPanelLauncher() {
  const { isChatOpen, toggleChat, isSupportOpen, toggleSupport } = usePanelStack()
  const isAiEnabled = useIsModuleEnabled('ai')

  return (
    <div className="flex items-center gap-[var(--sidebar-item-inset)]">
      {/* Every export of the person's, across tables and lists. */}
      <ExportsTray className={headerIconButtonClass} />
      {/* Every page can be shared, so sharing it is here, beside the panels. */}
      <PageShareButton className={headerIconButtonClass} />
      <IconButton
        onClick={(event) => toggleSupport({ alongside: isAlongsideClick(event) })}
        label={isSupportOpen ? 'Close support panel' : 'Contact support'}
        aria-pressed={isSupportOpen}
        tooltip={
          isSupportOpen
            ? 'Close support'
            : `Contact support (${modifierLabel}+click to keep AI Assistant open)`
        }
        wrapperClassName="shrink-0"
        className={headerIconButtonClass(isSupportOpen)}
      >
        <MailIcon size={18} aria-hidden="true" />
      </IconButton>
      {isAiEnabled && (
        <IconButton
          onClick={(event) => toggleChat({ alongside: isAlongsideClick(event) })}
          // The panel's code loads on the way to a click.
          onPointerEnter={() => void loadChatPanel().catch(() => undefined)}
          onFocus={() => void loadChatPanel().catch(() => undefined)}
          label={isChatOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
          aria-pressed={isChatOpen}
          tooltip={
            isChatOpen
              ? 'Close AI Assistant'
              : `AI Assistant (${modifierLabel}+click to keep Support open)`
          }
          wrapperClassName="shrink-0"
          className={headerIconButtonClass(isChatOpen)}
        >
          <SparkleChatIcon size={20} aria-hidden="true" />
        </IconButton>
      )}
    </div>
  )
}
